import React from 'react'

export interface InputProps {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date'
  name: string
  value?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  autoComplete?: string
  status?: 'default' | 'error' | 'success'
  icon?: React.ReactNode
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  className?: string
  /**
   * Associates the input with a descriptive element (e.g. helper/hint text or
   * an error message) so assistive technology announces the guidance together
   * with the field label. Forwarded verbatim to the underlying `<input>` as
   * `aria-describedby`.
   */
  'aria-describedby'?: string
  /** Explicit accessible label when no `<label htmlFor>` is adjacent. */
  'aria-label'?: string
  /**
   * Announces the current validation state to assistive technology. Set to
   * `true` when `status="error"` to pair a visible error border with the
   * corresponding aria semantics.
   */
  'aria-invalid'?: boolean
}

/**
 * Input -- Editorial Grid
 *
 * Styled text input with status variants (error/success border).
 * Optional left icon slot.
 */
export const Input: React.FC<InputProps> = ({
  type = 'text',
  name,
  value,
  placeholder,
  disabled = false,
  required = false,
  autoComplete,
  status = 'default',
  icon,
  onChange,
  onBlur,
  className = '',
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
}) => {
  const statusBorder: Record<string, string> = {
    default: 'border-eg-border focus-within:border-eg-primary',
    error: 'border-eg-danger focus-within:border-eg-danger',
    success: 'border-eg-success focus-within:border-eg-success',
  }

  return (
    <div
      className={[
        'relative flex items-center',
        'bg-eg-elevated border rounded-eg-md',
        'transition-colors duration-200',
        statusBorder[status],
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        className,
      ].join(' ')}
    >
      {icon && (
        <span className="pl-3 text-eg-text-muted flex-shrink-0">{icon}</span>
      )}
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete={autoComplete}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        aria-invalid={
          ariaInvalid !== undefined ? ariaInvalid : status === 'error' || undefined
        }
        onChange={onChange}
        onBlur={onBlur}
        className={[
          'w-full bg-transparent px-3 py-2.5',
          'font-body text-eg-md text-eg-text',
          'placeholder:text-eg-text-muted',
          'outline-none border-none',
          disabled ? 'cursor-not-allowed' : '',
        ].join(' ')}
      />
    </div>
  )
}

// ─── Textarea ───────────────────────────────────────────────────

export interface TextareaProps {
  name: string
  value?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  rows?: number
  status?: 'default' | 'error' | 'success'
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  className?: string
}

export const Textarea: React.FC<TextareaProps> = ({
  name,
  value,
  placeholder,
  disabled = false,
  required = false,
  rows = 4,
  status = 'default',
  onChange,
  onBlur,
  className = '',
}) => {
  const statusBorder: Record<string, string> = {
    default: 'border-eg-border focus-within:border-eg-primary',
    error: 'border-eg-danger focus-within:border-eg-danger',
    success: 'border-eg-success focus-within:border-eg-success',
  }

  return (
    <textarea
      name={name}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      rows={rows}
      onChange={onChange}
      onBlur={onBlur}
      className={[
        'w-full bg-eg-elevated border rounded-eg-md px-3 py-2.5',
        'font-body text-eg-md text-eg-text',
        'placeholder:text-eg-text-muted',
        'outline-none resize-y',
        'transition-colors duration-200',
        statusBorder[status],
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        className,
      ].join(' ')}
    />
  )
}

// ─── Select ─────────────────────────────────────────────────────

export interface SelectOption {
  label: string
  value: string
}

export interface SelectProps {
  /**
   * Optional explicit `id` attribute for the underlying `<select>` element.
   * When omitted, it defaults to `name` so a `<label htmlFor={name}>` renders
   * correctly associated. Providing `id` explicitly lets callers use
   * `useId()`-generated values without colliding with the semantic `name`.
   */
  id?: string
  name: string
  value?: string
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  required?: boolean
  status?: 'default' | 'error' | 'success'
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
  className?: string
}

export const Select: React.FC<SelectProps> = ({
  id,
  name,
  value,
  options,
  placeholder,
  disabled = false,
  required = false,
  status = 'default',
  onChange,
  className = '',
}) => {
  const statusBorder: Record<string, string> = {
    default: 'border-eg-border focus:border-eg-primary',
    error: 'border-eg-danger focus:border-eg-danger',
    success: 'border-eg-success focus:border-eg-success',
  }

  return (
    <select
      id={id ?? name}
      name={name}
      value={value}
      disabled={disabled}
      required={required}
      onChange={onChange}
      className={[
        'w-full bg-eg-elevated border rounded-eg-md px-3 py-2.5',
        'font-body text-eg-md text-eg-text',
        'outline-none cursor-pointer appearance-none',
        'transition-colors duration-200',
        statusBorder[status],
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        className,
      ].join(' ')}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}
